import type { CapacityOperands } from "@hakili/contracts";
import { CONSULTABLE_DOCUMENT_CODES } from "@hakili/domain-types";
import { formatDate, formatDateTime, formatMoney, t } from "@hakili/i18n";
import { GuardedButton } from "@hakili/ui";
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, Sparkles } from "lucide-react";

import { causeSentence } from "../cause-sentence.js";

/**
 * Round 11a (`OD-37`, ruling D-1010-3): **« Analyse de Hakili » on the dossier , the console run, read by the chargée.**
 *
 * The run happens once, at the console, on her own act (« Lancer l'analyse Hakili », WF-CON-11 bis), once every field of
 * the reading carries her confirmation (A50). The panel prints what the door returns, in this order: the register of
 * findings first (bloquant, à lever, signalé, as the door orders them), then the grade card with its operands and the
 * word « provisoire » while readings are missing, then the avis written for her. There is NO offer: the run has no bank
 * and no grid. At `analyse` the panel says « Analyse Hakili en cours » until the run completes. Nothing here decides:
 * the gate is the door's, printed in its own words before the door says them.
 */
/** `P2`: where a finding stands, as the door returns it: a received pièce's page, or the figures compared across pièces. */
export type ConsoleEvidence =
  | {
      readonly kind: "document";
      readonly documentId: string | null;
      readonly documentCode: string;
      readonly page: number;
      readonly box: unknown;
    }
  | {
      readonly kind: "figures";
      readonly compared: Readonly<Record<string, number | string | null>>;
      /** `QUOTITE-OPERANDS`: the quotité point's operands, on the same contract the bank's door answers on. */
      readonly operands?: CapacityOperands;
    };

export interface ConsoleFindingView {
  /** `P2`: `point | step | screening | delegation`, which with `code` is the register's key of the finding (`source.code`). */
  readonly source?: string;
  readonly evidence?: ConsoleEvidence | null;
  readonly code: string;
  readonly verdict: "conforme" | "non_conforme" | "incertain";
  readonly reason: string;
  readonly severityClass: "bloquant" | "a_lever" | "signale";
}

export interface ConsoleUnderwritingView {
  readonly run: {
    readonly id: string;
    readonly status: "en_attente" | "en_cours" | "terminee";
    readonly outcome: "reussie" | "en_echec" | null;
    /** `ARC2-DATA` (item 3): the instant the pièces were really read, as the door gives it (`null` where it does not know); absent before this round. */
    readonly documentsReadAt?: string | null;
  };
  readonly registre: readonly ConsoleFindingView[] | null;
  readonly grade: {
    /** `null` while the note is incomplète (the capacity could not be computed): no letter, no mean. */
    readonly grade: string | null;
    readonly average: number | null;
    /** `CAPACITE-NON-CALCULEE`: the state and its cause in Hakili's words, the same on the bank's card; `null` when the capacity was computed. */
    readonly incomplete?: { readonly cause: string; readonly words: string } | null;
    readonly provisional: boolean;
    readonly unread: readonly { readonly component: string }[];
    readonly components: readonly {
      readonly key: string;
      /** `null` for the Capacité that could not be computed. */
      readonly score: number | null;
      readonly operands: string;
      readonly note: string;
    }[];
  } | null;
  readonly opinion: {
    readonly text: string;
    /** `P2b` (`souscription-avis@8`): the avis in one sentence and its steps; absent on an avis stored in one text. */
    readonly summary?: string;
    readonly steps?: readonly { readonly finding: string; readonly label: string; readonly text: string }[];
    readonly recommendation: "favorable" | "reserve" | "defavorable";
    /** The chargée's own act on the avis, when she has confirmed or corrected it. */
    readonly act?: {
      readonly act: "confirmee" | "corrigee";
      readonly byName: string;
      readonly actedAt: string;
      readonly value?: string | null;
      readonly screening?: unknown;
    } | null;
  } | null;
  /** `OD-37` round 9c-2b (L-6): the readings behind the number. The console run has no rate, term or instalment. */
  readonly card?: {
    readonly readings: readonly {
      readonly code: string;
      readonly reason: string;
      readonly operands: readonly string[];
    }[];
    readonly capacityObserved: { readonly amount: number; readonly derivation: string } | null;
    readonly fees: { readonly average: number | null } | null;
  } | null;
  readonly failure: string | null;
}

/**
 * `AA-3c` (the chain and the one button): **where the analysis of the file stands, as the door says it.** The state is the
 * door's (derived from the runs, never stored); `stale` says the analysis rests on figures changed since (A47); `request`
 * is the ONE button's own answer , « Lancer » or « Relancer l'analyse Hakili », greyed with the sentence the press itself
 * would refuse with. The screen prints all of it and recomputes none.
 */
export type AnalysisChainState = "lecture" | "a_confirmer" | "analyse" | "terminee" | "arretee";

/**
 * `AA-4a`: the chain's five links, each derived by the door from the runs and the dossier's kept filter computation, and the
 * clock of the link that runs. The bar draws three steps from the links (the filter and the banks are the tail of « Analyse »).
 */
export type ChainLinkKey = "reading" | "confirmation" | "underwriting" | "filter" | "banks";
export type ChainLinkStatus = "done" | "current" | "pending" | "failed";

export interface AnalysisChainView {
  readonly chain: {
    readonly id: string;
    readonly state: AnalysisChainState;
    readonly stale: boolean;
    readonly readingRunId: string;
    readonly underwritingRunId: string | null;
    readonly startedAt: string;
    readonly links: readonly {
      readonly key: ChainLinkKey;
      readonly status: ChainLinkStatus;
      /** The failure in the link's own words (`failed`), else `null`. */
      readonly words: string | null;
    }[];
  } | null;
  /** The running link's clock: the SERVER's now and the run's start, so the browser's clock decides nothing. */
  readonly timing: {
    readonly link: "reading" | "underwriting";
    readonly startedAt: string | null;
    readonly now: string;
    readonly expectedMs: number;
    readonly estimate: boolean;
  } | null;
  /** The sentence beside the state: why it waits, why it stopped, that it rests on changed figures. */
  readonly words: string | null;
  readonly request: {
    readonly kind: "lancer" | "relancer";
    readonly allowed: boolean;
    readonly reason: string | null;
  };
  /** Earlier runs only, newest first. */
  readonly history: readonly {
    readonly runId: string;
    readonly kind: "lecture" | "analyse";
    readonly submittedAt: string;
    readonly endedAt: string | null;
    readonly outcome: "reussie" | "en_echec" | null;
  }[];
}

const STATE_WORDS = new Map<AnalysisChainState, string>([
  ["lecture", "console.analyse.etatLecture"],
  ["a_confirmer", "console.analyse.etatAConfirmer"],
  ["analyse", "console.analyse.etatAnalyse"],
  ["terminee", "console.analyse.etatTerminee"],
  ["arretee", "console.analyse.etatArretee"],
]);

/** The three steps of the bar, and the links each one stands for (`AA-4a`: « Analyse » carries the filter and the banks). */
const STEPS: readonly {
  readonly key: string;
  readonly words: string;
  readonly links: readonly ChainLinkKey[];
}[] = [
  { key: "lecture", words: "console.analyse.etapeLecture", links: ["reading"] },
  { key: "confirmation", words: "console.analyse.etapeConfirmation", links: ["confirmation"] },
  { key: "analyse", words: "console.analyse.etapeAnalyse", links: ["underwriting", "filter", "banks"] },
];

type StepMark = "done" | "current" | "failed" | "upcoming";

/** One step from its links: a failed link fails it, all done completes it, one running (or begun) makes it the current one. */
function markOf(
  links: readonly { readonly key: ChainLinkKey; readonly status: ChainLinkStatus }[],
  keys: readonly ChainLinkKey[],
): StepMark {
  const own = links.filter((one) => keys.includes(one.key));
  if (own.some((one) => one.status === "failed")) return "failed";
  if (own.length > 0 && own.every((one) => one.status === "done")) return "done";
  if (own.some((one) => one.status === "current" || one.status === "done")) return "current";
  return "upcoming";
}

const TAIL_WORDS = new Map<string, string>([
  ["filter", "console.analyse.queueFiltre"],
  ["banks", "console.analyse.queueBanques"],
]);

/** « 1 min 12 s »: the elapsed time, whole seconds. */
function spoken(ms: number): string {
  const seconds = Math.max(0, Math.trunc(ms / 1000));
  return seconds < 60
    ? t("console.analyse.dureeSecondes").replace("{n}", String(seconds))
    : t("console.analyse.dureeMinSec")
        .replace("{m}", String(Math.trunc(seconds / 60)))
        .replace("{s}", String(seconds % 60));
}

/** « environ 101 s » from past runs, « environ 2 minutes » for the estimate until five runs have ended. */
function expectedWords(ms: number, estimate: boolean): string {
  const seconds = Math.trunc(ms / 1000);
  const duration =
    estimate || seconds >= 600
      ? t("console.analyse.dureeMinutes").replace("{n}", String(Math.trunc(seconds / 60)))
      : t("console.analyse.dureeSecondes").replace("{n}", String(seconds));
  return t("console.analyse.environ").replace("{duree}", duration);
}

const OUTCOME_WORDS = new Map<string, string>([
  ["reussie", "console.analyse.historiqueReussie"],
  ["en_echec", "console.analyse.historiqueEchec"],
]);

const CLASS_WORDS = new Map<string, string>([
  ["bloquant", "console.analyse.classeBloquant"],
  ["a_lever", "console.analyse.classeALever"],
  ["signale", "console.analyse.classeSignale"],
]);
const CLASS_TONE = new Map<string, string>([
  ["bloquant", "bg-rose-50 text-rose-700"],
  ["a_lever", "bg-amber-50 text-amber-700"],
  ["signale", "bg-gray-100 text-gray-600"],
]);
const COMPONENT_WORDS = new Map<string, string>([
  ["incomeStability", "bank.hakili.composanteRevenus"],
  ["collateral", "bank.hakili.composanteGaranties"],
  ["bankingHistory", "bank.hakili.composanteHistorique"],
  ["deposit", "bank.hakili.composanteApport"],
  ["capacity", "bank.hakili.composanteCapacite"],
]);
const RECOMMENDATION_WORDS = new Map<string, string>([
  ["favorable", "console.analyse.avisFavorable"],
  ["reserve", "console.analyse.avisReserve"],
  ["defavorable", "console.analyse.avisDefavorable"],
]);

/** `P2`: how her decisions stand: `results` (all three), or `without` the analysis (it failed, or it cannot run). */
export type DecisionsMode = "results" | "without";

/** `P2`: a pièce as the panel names it: the label the door gave, else the catalogue's, never its code. */
interface PanelDocument {
  readonly code: string;
  readonly label: string;
  readonly received: boolean;
}

const DOCUMENT_FALLBACK: ReadonlyMap<string, string> = new Map([
  ["pieceIdentite", "console.analyse.pieceNomIdentite"],
  ["bulletinsSalaire", "console.analyse.pieceNomBulletins"],
  ["relevesBancaires", "console.analyse.pieceNomReleves"],
  ["attestationTravail", "console.analyse.pieceNomAttestation"],
  ["factureProforma", "console.analyse.pieceNomProforma"],
  ["rapportExpertise", "console.analyse.pieceNomExpertise"],
]);

/**
 * `P2`: the pièces a finding stands on when its evidence names no single pièce (a comparison of figures, or none): the counters read these
 * pièces, so the panel says them in words and draws no button for them.
 */
const FINDING_DOCUMENTS: ReadonlyMap<string, readonly string[]> = new Map([
  ["cni", ["pieceIdentite"]],
  ["kyc", ["pieceIdentite"]],
  ["bulletins", ["bulletinsSalaire"]],
  ["releves", ["relevesBancaires"]],
  ["attestationTravail", ["attestationTravail"]],
  ["proforma", ["factureProforma"]],
  ["rapportExpertise", ["rapportExpertise"]],
  ["prixValeurExpertisee", ["factureProforma", "rapportExpertise"]],
  ["salaireDeclareObserve", ["bulletinsSalaire", "relevesBancaires"]],
  ["employeurAnciennete", ["attestationTravail", "bulletinsSalaire"]],
  ["engagementsNonDeclares", ["relevesBancaires", "bulletinsSalaire"]],
  ["apportOrigine", ["relevesBancaires"]],
  ["salaryRegularity", ["relevesBancaires"]],
  ["monthEndPosition", ["relevesBancaires"]],
  ["incidents", ["relevesBancaires"]],
  ["bankingProfile", ["relevesBancaires"]],
  ["employmentEstimation", ["attestationTravail", "bulletinsSalaire"]],
  ["contractTerm", ["attestationTravail"]],
  ["seniority", ["attestationTravail"]],
  ["retirementAge", ["pieceIdentite"]],
  ["incomeComposition", ["bulletinsSalaire", "relevesBancaires"]],
]);

const documentsOf = (finding: {
  readonly code: string;
  readonly evidence?: ConsoleEvidence | null | undefined;
}): readonly string[] =>
  finding.evidence?.kind === "document"
    ? [finding.evidence.documentCode]
    : (FINDING_DOCUMENTS.get(finding.code) ?? []);

const documentName = (code: string, pieces: readonly PanelDocument[]): string | null => {
  const given = pieces.find((one) => one.code === code)?.label;
  if (given !== undefined) return given;
  const key = DOCUMENT_FALLBACK.get(code);
  return key === undefined ? null : t(key);
};

/** The finding of a step: the register's entry whose key (`source.code`) the step carries. */
const findingOf = (
  key: string,
  registre: ConsoleUnderwritingView["registre"],
): { readonly code: string; readonly evidence?: ConsoleEvidence | null | undefined } => {
  const found = registre?.find((one) => `${one.source ?? "point"}.${one.code}` === key);
  return found ?? { code: key.slice(key.indexOf(".") + 1) };
};

/**
 * `P2`: the pièces a finding stands on, named in words, and « Voir la pièce » where it stands on ONE received pièce the chargée may open. A
 * control with no door is not drawn (D-1019-2): no door to open pièces, a pièce not received, a comparison of figures.
 */
function DocumentLine(props: {
  readonly testId: string;
  readonly finding: { readonly code: string; readonly evidence?: ConsoleEvidence | null | undefined };
  readonly pieces: readonly PanelDocument[];
  readonly onOpenDocument: ((documentCode: string) => Promise<string | null>) | undefined;
}): React.JSX.Element | null {
  const names = documentsOf(props.finding).flatMap((code) => {
    const name = documentName(code, props.pieces);
    return name === null ? [] : [name];
  });
  if (names.length === 0) return null;
  const evidence = props.finding.evidence;
  const openable =
    evidence?.kind === "document" &&
    CONSULTABLE_DOCUMENT_CODES.includes(evidence.documentCode) &&
    props.pieces.some((one) => one.code === evidence.documentCode && one.received) &&
    props.onOpenDocument !== undefined;
  return (
    <span className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500">
      <span data-testid={`${props.testId}-pieces`}>
        {t(names.length === 1 ? "console.analyse.pieceUne" : "console.analyse.piecesPlusieurs").replace(
          "{pieces}",
          names.join(", "),
        )}
      </span>
      {openable ? (
        <button
          type="button"
          data-testid={`${props.testId}-voir`}
          onClick={() => {
            void props.onOpenDocument?.(evidence.documentCode).then((url) => {
              if (url !== null) window.open(url, "_blank", "noopener,noreferrer");
            });
          }}
          className="flex items-center gap-1 text-sm font-medium text-indigo-700 hover:text-indigo-900"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          {t("console.dossier.piecesVoir")}
        </button>
      ) : null}
    </span>
  );
}

const STATE_TONE = new Map<string, string>([
  ["favorable", "bg-emerald-50 text-emerald-700"],
  ["reserve", "bg-amber-50 text-amber-700"],
  ["defavorable", "bg-rose-50 text-rose-700"],
]);

/** `P2`: the grade as a compact card: the letter and the mean first, one line a component with its score; the operands are in the folded detail. */
function GradeCard(props: {
  readonly grade: NonNullable<ConsoleUnderwritingView["grade"]>;
}): React.JSX.Element {
  const { grade } = props;
  return (
    <div data-testid="analyse-note" className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-sm font-medium text-gray-700">{t("console.analyse.noteTitre")}</p>
      <p className="mt-0.5 text-2xl font-semibold text-gray-900" data-testid="analyse-note-lettre">
        {(grade.incomplete ?? null) !== null
          ? t("console.analyse.noteIncomplete")
          : `${grade.grade ?? ""} · ${String(grade.average).replace(".", ",")}`}
      </p>
      {(grade.incomplete ?? null) !== null ? (
        <p className="mt-0.5 flex items-center gap-2 text-sm text-amber-700" data-testid="analyse-incomplete">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
          {t("console.analyse.noteIncompleteExplication")}
        </p>
      ) : null}
      {grade.provisional ? (
        <p className="mt-0.5 flex items-center gap-2 text-sm text-amber-700" data-testid="analyse-provisoire">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
          {grade.unread.length === 1
            ? t("console.analyse.noteProvisoireUne")
            : t("console.analyse.noteProvisoirePlusieurs").replace("{n}", String(grade.unread.length))}
        </p>
      ) : null}
      <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-900 sm:grid-cols-3">
        {grade.components.map((one) => (
          <li key={one.key} data-testid={`analyse-composante-${one.key}`}>
            {t(COMPONENT_WORDS.get(one.key) ?? "bank.hakili.composanteCapacite")} ·{" "}
            {one.score === null
              ? causeSentence(t("console.analyse.capaciteNonCalculee"), grade.incomplete?.words ?? "")
              : String(one.score).replace(".", ",")}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * `P2`: the avis , its state first (« À vérifier »), then ONE sentence, then the steps as a numbered list addressed to « vous », each tied to its
 * finding by the register's key, with the label the door rendered and the pièce it stands on. An avis stored in one text (every earlier
 * version, and the bank's) keeps rendering as one paragraph.
 */
function OpinionBlock(props: {
  readonly opinion: NonNullable<ConsoleUnderwritingView["opinion"]>;
  readonly registre: ConsoleUnderwritingView["registre"];
  readonly pieces: readonly PanelDocument[];
  readonly onOpenDocument: ((documentCode: string) => Promise<string | null>) | undefined;
}): React.JSX.Element {
  const { opinion } = props;
  const inSteps = opinion.summary !== undefined && opinion.steps !== undefined;
  // Her own text, when she corrected the avis: it leads, and the model's proposal folds under « Proposition de Hakili ».
  const corrected =
    opinion.act?.act === "corrigee" && (opinion.act.value ?? "").trim() !== "" ? opinion.act.value : null;
  const proposal = inSteps ? (
    <>
      <p className="mt-1 text-sm leading-relaxed text-gray-900" data-testid="analyse-avis-resume">
        {opinion.summary}
      </p>
      <ol
        className="mt-2 list-decimal space-y-2 pl-5 text-sm text-gray-900"
        data-testid="analyse-avis-etapes"
      >
        {(opinion.steps ?? []).map((step, at) => (
          <li
            key={`${step.finding}-${String(at)}`}
            data-testid={`analyse-avis-etape-${String(at)}`}
            data-finding={step.finding}
          >
            <span className="font-medium">{step.label}</span> · {step.text}
            <DocumentLine
              testId={`analyse-avis-etape-${String(at)}`}
              finding={findingOf(step.finding, props.registre)}
              pieces={props.pieces}
              onOpenDocument={props.onOpenDocument}
            />
          </li>
        ))}
      </ol>
    </>
  ) : (
    <p className="mt-1 text-sm leading-relaxed text-gray-900" data-testid="analyse-avis-texte">
      {opinion.text}
    </p>
  );
  return (
    <div
      data-testid="analyse-avis"
      {...(opinion.act === undefined || opinion.act === null ? {} : { "data-row-state": "done" })}
      className="rounded-xl border border-gray-200 bg-white px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-gray-700">{t("console.analyse.avisTitre")}</p>
        <span
          data-testid="analyse-avis-etat"
          data-recommendation={opinion.recommendation}
          className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${STATE_TONE.get(opinion.recommendation) ?? ""}`}
        >
          {t(RECOMMENDATION_WORDS.get(opinion.recommendation) ?? "console.analyse.avisReserve")}
        </span>
      </div>
      {corrected === null ? (
        proposal
      ) : (
        <>
          <p
            className="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-900"
            data-testid="analyse-avis-texte-corrige"
          >
            {corrected}
          </p>
          <details className="mt-2" data-testid="analyse-avis-proposition">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              {t("console.analyse.propositionHakili")}
            </summary>
            {proposal}
          </details>
        </>
      )}
      {opinion.act === undefined || opinion.act === null ? null : (
        <p className="mt-2 text-xs text-gray-500" data-testid="analyse-avis-acte">
          {t("console.analyse.avisActe")
            .replace(
              "{acte}",
              t(
                opinion.act.act === "confirmee"
                  ? "console.analyse.acteConfirmee"
                  : "console.analyse.acteCorrigee",
              ),
            )
            .replace("{auteur}", opinion.act.byName)
            .replace("{date}", formatDate(new Date(opinion.act.actedAt)))}
        </p>
      )}
    </div>
  );
}

export function UnderwritingSection(props: {
  readonly view: ConsoleUnderwritingView | null;
  /** The dossier stands at `analyse`: the control is offered, and the « en cours » line is said. */
  readonly atAnalysis: boolean;
  /** The chargée may act: her file, an open stage. */
  readonly canAct: boolean;
  readonly busy: boolean;
  /** `AA-3c`: where the chain stands and the ONE button's answer; `undefined`/`null` where the door was not handed. */
  readonly analysis?: AnalysisChainView | null | undefined;
  readonly refusal?: string | null | undefined;
  readonly onLaunch: () => void;
  /** `P2`: the dossier's pièces by their code, with the label the door gave and whether the pièce was received (a button opens a received one only). */
  readonly pieces?: readonly { readonly code: string; readonly label: string; readonly received: boolean }[];
  /** `P2`: opens a pièce (a presigned URL, `null` when none could be had); absent, no « Voir la pièce » is drawn. */
  readonly onOpenDocument?: ((documentCode: string) => Promise<string | null>) | undefined;
  /**
   * `P2`: the chargée's decisions, drawn last. With the results, all three. When the analysis failed or cannot run (the live reading switch off:
   * `intelligence_live_reading`, the provider answers « lecture indisponible »), she decides WITHOUT it , the caller draws the two that need no
   * analysis, under a neutral line, and « Valider » unavailable with its reason. None while there is no analysis or one is in flight.
   */
  readonly decisions?: ((mode: DecisionsMode) => React.ReactNode) | undefined;
}): React.JSX.Element {
  const { view } = props;
  const inFlight = view !== null && view.run.status !== "terminee";
  const chain = props.analysis?.chain ?? null;
  const timing = props.analysis?.timing ?? null;
  // No analysis yet or one in flight: no decision, the single button. Results: the three. Failed , or stopped before it could run: without it.
  const decisionsMode: DecisionsMode | null =
    view !== null
      ? view.run.status !== "terminee"
        ? null
        : view.run.outcome === "reussie"
          ? "results"
          : view.run.outcome === "en_echec"
            ? "without"
            : null
      : chain?.state === "arretee"
        ? "without"
        : null;
  // `AA-4a`: the panel polls nothing. ONE poller reads the screen again while anything moves (the route's, `useAnalysisPolling`).
  return (
    <section data-testid="analyse-hakili" className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-indigo-600" aria-hidden="true" />
        <h3 className="text-sm font-semibold">{t("console.analyse.titre")}</h3>
      </div>
      {props.refusal !== null && props.refusal !== undefined ? (
        <p role="alert" data-testid="analyse-refus" className="mb-2 text-sm text-rose-600">
          {props.refusal}
        </p>
      ) : null}
      {chain === null ? null : (
        <div className="mb-3" data-testid="analyse-chaine">
          {/* The state, said ONCE: the door's words when it has some, else the state's. A status region from the first
              render, so the end of the run (A47) is announced by the same node that said it was running. */}
          <p
            className={`text-sm ${chain.stale ? "font-medium text-amber-700" : "font-medium text-gray-900"}`}
            role="status"
            data-testid="analyse-etat"
            data-stale={chain.stale ? "true" : "false"}
          >
            {props.analysis?.words ?? t(STATE_WORDS.get(chain.state) ?? "console.analyse.etatAnalyse")}
          </p>
          <ol
            className="mt-2 grid grid-cols-3 gap-2 text-sm text-gray-700"
            data-testid="analyse-progression"
            aria-label={t("console.analyse.progressionTitre")}
          >
            {STEPS.map((one) => {
              const mark = markOf(chain.links, one.links);
              return (
                <li
                  key={one.key}
                  data-testid={`analyse-etape-${one.key}`}
                  data-step={mark}
                  className={
                    mark === "done"
                      ? "font-medium text-emerald-700"
                      : mark === "failed"
                        ? "font-medium text-rose-700"
                        : mark === "current"
                          ? "font-medium text-gray-900"
                          : "text-gray-500"
                  }
                  {...(mark === "current" ? { "aria-current": "step" as const } : {})}
                >
                  <span
                    aria-hidden="true"
                    className={`mb-1 block h-1.5 rounded-full ${
                      mark === "done"
                        ? "bg-emerald-500"
                        : mark === "failed"
                          ? "bg-rose-500"
                          : mark === "current"
                            ? "animate-pulse bg-indigo-500"
                            : "bg-gray-200"
                    }`}
                  />
                  {mark === "done" ? "✓ " : ""}
                  {t(one.words)}
                </li>
              );
            })}
          </ol>
          {(() => {
            const failed = chain.links.find((one) => one.status === "failed" && TAIL_WORDS.has(one.key));
            return failed === undefined ? null : (
              <p className="mt-1 text-xs text-rose-700" data-testid="analyse-queue" data-link={failed.key}>
                {t(TAIL_WORDS.get(failed.key) ?? "console.analyse.queueFiltre")}
              </p>
            );
          })()}
          {timing === null ? null : (
            <p className="mt-1.5 text-xs text-gray-600" data-testid="analyse-temps">
              {timing.startedAt === null ? null : (
                <>
                  {t("console.analyse.ecoule")}{" "}
                  <span data-testid="analyse-ecoule">
                    {spoken(Date.parse(timing.now) - Date.parse(timing.startedAt))}
                  </span>
                  {" · "}
                </>
              )}
              {t("console.analyse.attendu")}{" "}
              <span data-testid="analyse-attendu" data-estimate={timing.estimate ? "true" : "false"}>
                {expectedWords(timing.expectedMs, timing.estimate)}
              </span>
              {timing.estimate ? (
                <>
                  {" "}
                  <span data-testid="analyse-attendu-estimation">{t("console.analyse.estimation")}</span>
                </>
              ) : null}
            </p>
          )}
        </div>
      )}
      {view === null ? (
        <p className="text-sm text-gray-500" data-testid="analyse-absente">
          {t("console.analyse.absente")}
        </p>
      ) : inFlight ? (
        chain !== null ? null : (
          <p className="flex items-center gap-2 text-sm text-gray-500" data-testid="analyse-en-cours">
            <Clock className="h-4 w-4 text-amber-500" aria-hidden="true" />
            {t("console.analyse.enCours")}
          </p>
        )
      ) : (
        <div className="flex flex-col gap-4">
          {view.run.outcome === "en_echec" ? (
            <p
              className="rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700"
              data-testid="analyse-echec"
            >
              {t("console.analyse.echec").replace("{motif}", view.failure ?? "")}
            </p>
          ) : null}

          {view.grade === null ? null : <GradeCard grade={view.grade} />}

          {view.opinion === null ? null : (
            <OpinionBlock
              opinion={view.opinion}
              registre={view.registre}
              pieces={props.pieces ?? []}
              onOpenDocument={props.onOpenDocument}
            />
          )}

          <details
            className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5"
            data-testid="analyse-detail"
          >
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              {t("console.analyse.detailTitre")}
            </summary>
            <div className="mt-2 flex flex-col gap-3">
              <div data-testid="analyse-registre">
                <p className="text-sm font-medium text-gray-700">{t("console.analyse.registreTitre")}</p>
                {view.registre === null || view.registre.length === 0 ? (
                  <p
                    className="mt-1 flex items-center gap-2 text-sm text-gray-500"
                    data-testid="analyse-registre-vide"
                  >
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                    {t("console.analyse.registreVide")}
                  </p>
                ) : (
                  <ul className="mt-1.5 space-y-2">
                    {view.registre.map((one, index) => (
                      <li
                        key={`${one.code}-${String(index)}`}
                        className="flex items-start gap-2 text-sm"
                        data-testid={`analyse-constat-${one.code}`}
                        data-classe={one.severityClass}
                      >
                        <span
                          className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${CLASS_TONE.get(one.severityClass) ?? ""}`}
                        >
                          {t(CLASS_WORDS.get(one.severityClass) ?? "console.analyse.classeSignale")}
                        </span>
                        <span className="flex flex-col gap-0.5">
                          <span className="text-gray-900">{one.reason}</span>
                          <DocumentLine
                            testId={`analyse-constat-${one.code}`}
                            finding={one}
                            pieces={props.pieces ?? []}
                            onOpenDocument={props.onOpenDocument}
                          />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {view.card === undefined || view.card === null ? null : (
                <div data-testid="analyse-lectures">
                  <p className="text-sm font-medium text-gray-700">{t("console.analyse.lecturesTitre")}</p>
                  <ul className="mt-1.5 space-y-1.5">
                    {view.card.readings.map((one) => (
                      <li key={one.code} data-testid={`analyse-lecture-${one.code}`}>
                        <p className="text-sm text-gray-900">{one.reason}</p>
                        {one.operands.map((operand) => (
                          <p key={operand} className="text-sm text-gray-500">
                            {operand}
                          </p>
                        ))}
                      </li>
                    ))}
                  </ul>
                  {view.card.capacityObserved === null ? null : (
                    <p className="mt-1.5 text-sm text-gray-900" data-testid="analyse-capacite-observee">
                      {t("console.analyse.capaciteObservee")} {formatMoney(view.card.capacityObserved.amount)}
                    </p>
                  )}
                  {typeof view.card.fees?.average !== "number" ? null : (
                    <p className="text-sm text-gray-900" data-testid="analyse-charges">
                      {t("console.analyse.charges")} {formatMoney(view.card.fees.average)}
                    </p>
                  )}
                </div>
              )}

              {view.grade === null ? null : (
                <div data-testid="analyse-detail-composantes">
                  <p className="text-sm font-medium text-gray-700">
                    {t("console.analyse.composantesDetail")}
                  </p>
                  <ul className="mt-1.5 space-y-1.5">
                    {view.grade.components.map((one) => (
                      <li key={one.key} data-testid={`analyse-detail-composante-${one.key}`}>
                        <p className="text-sm text-gray-900">
                          {t(COMPONENT_WORDS.get(one.key) ?? "bank.hakili.composanteCapacite")} ·{" "}
                          {one.score === null
                            ? causeSentence(
                                t("console.analyse.capaciteNonCalculee"),
                                view.grade?.incomplete?.words ?? "",
                              )
                            : String(one.score).replace(".", ",")}
                        </p>
                        <p className="text-sm text-gray-500">{one.operands}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        </div>
      )}
      {decisionsMode !== null && props.decisions !== undefined ? (
        <div className="mt-4" data-testid="analyse-decisions" data-mode={decisionsMode}>
          {props.decisions(decisionsMode)}
        </div>
      ) : null}
      {props.atAnalysis && props.canAct && props.analysis !== null && props.analysis !== undefined ? (
        <div className="mt-3" data-testid="analyse-demande">
          {props.busy || !props.analysis.request.allowed ? (
            <GuardedButton
              disabled
              testId="analyse-lancer"
              variant="secondary"
              reason={props.analysis.request.reason ?? t("console.analyse.enCours")}
            >
              {t(
                props.analysis.request.kind === "lancer"
                  ? "console.analyse.lancer"
                  : "console.analyse.relancer",
              )}
            </GuardedButton>
          ) : (
            <GuardedButton testId="analyse-lancer" variant="secondary" onClick={props.onLaunch}>
              {t(
                props.analysis.request.kind === "lancer"
                  ? "console.analyse.lancer"
                  : "console.analyse.relancer",
              )}
            </GuardedButton>
          )}
        </div>
      ) : null}
      {props.analysis === null ||
      props.analysis === undefined ||
      props.analysis.history.length === 0 ? null : (
        <details className="mt-3" data-testid="analyse-historique">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            {t("console.analyse.historiqueTitre")} ({String(props.analysis.history.length)})
          </summary>
          <ul className="mt-1.5 space-y-1 text-sm text-gray-700">
            {props.analysis.history.map((one) => (
              <li key={one.runId} data-testid={`analyse-historique-${one.runId}`}>
                {t(
                  one.kind === "lecture"
                    ? "console.analyse.historiqueLecture"
                    : "console.analyse.historiqueAnalyse",
                )}{" "}
                · {formatDateTime(new Date(one.submittedAt))} ·{" "}
                {t(OUTCOME_WORDS.get(one.outcome ?? "") ?? "console.analyse.historiqueEnCours")}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
